import { sortBy } from 'lodash';
import { Box } from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import { CellProps, Column, Renderer } from 'react-table';
import { toEuro } from '../../../../../../../../../sales-conversation/src/utils/stringUtils';
import {
  Coverage,
  InsurancePolicy,
} from '../../../../../../../types/AdvisoryReport';
import useAdvisoryReportCurrency from '../../../../../hooks/useAdvisoryReportCurrency';
import useIsNLplan from '../../../../../../../../../common/src/hooks/useIsNLPlan';

interface ReturnProps {
  // Typing is really bad for react table, so I opted for an any here so
  // that I am able to use the custom sort.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: Column<any>[];
  data: Coverage[];
}

type CellRenderer = Renderer<CellProps<Coverage>>;

const useInsurancePolicyRisksTable = (
  insurancePolicy: InsurancePolicy,
): ReturnProps => {
  const { t } = useTranslation();
  const { isNL } = useIsNLplan();
  const { currency } = useAdvisoryReportCurrency();
  const isTailoredPremium = !!insurancePolicy.is_tailored_premium;
  const RenderCurrencyCell: CellRenderer = ({ cell }) => (
    <Box>
      {isTailoredPremium ? (
        <>-</>
      ) : cell.value != null ? (
        <>{toEuro(cell.value, currency)}</>
      ) : (
        <>-</>
      )}
    </Box>
  );

  const renderRiskCell: CellRenderer = ({ cell }) =>
    cell.value ? (
      <Box w="28">{cell.value}</Box>
    ) : (
      <Box w="28">{t('OTHER')}</Box>
    );

  const renderCell: CellRenderer = ({ cell }) => (
    <Box>{cell.value ? <>{cell.value}</> : <>-</>}</Box>
  );
  const renderCoverageCell: CellRenderer = ({ cell }) => (
    <Box w="80">{cell.value ? <>{cell.value}</> : <>-</>}</Box>
  );

  // Sorted needed for row spans
  const dataWithSortedCoverages = sortBy(
    insurancePolicy.coverages,
    'risk_object',
  );

  const columns = [
    {
      Header: t('RISK'),
      accessor: 'risk_object',
      disableSortBy: true,
      Cell: renderRiskCell,
      isRowSpanned: true,
    },
    {
      Header: t('COVERAGE'),
      accessor: 'name',
      disableSortBy: true,
      Cell: renderCoverageCell,
    },
    {
      Header: t(
        'ADVISORY-CONVERSATION_NEW-REPORT_OFFERS_FORM_INSURED-CONTENT_LABEL',
      ),
      accessor: 'insured_value',
      disableSortBy: true,
      Cell: RenderCurrencyCell,
    },
    {
      Header: t('ADVISORY-REPORTS_DETAIL_OFFERS_OWN-RISK'),
      accessor: 'deductible',
      disableSortBy: true,
      Cell: renderCell,
    },
    {
      Header: t('PREMIUM'),
      accessor: 'premium',
      disableSortBy: true,
      Cell: RenderCurrencyCell,
    },
  ];
  if (
    isNL &&
    (insurancePolicy.status === 'INACTIVE' ||
      insurancePolicy.type === 'INACTIVE')
  ) {
    columns.push({
      Header: t('COMMISSION'),
      accessor: 'commission',
      disableSortBy: true,
      Cell: RenderCurrencyCell,
    });
  }

  return {
    columns: columns,
    data: dataWithSortedCoverages || [],
  };
};

export default useInsurancePolicyRisksTable;
